// =============================================================================
// AGENT 4 — STRATEG KONWERSJI: EKRAN WYNIKÓW
// Łączy ScoreResult (z Agent 3) z Explanation i LeadForm.
// =============================================================================

import { useState, useEffect } from 'react';
import './Results.css';
import { useGame } from '../../store/gameStore';
import { buildExplanationPayload } from '../../engine/explanationBuilder';
import { ExplanationPanel } from './ExplanationPanel';
import { LeadForm } from './LeadForm';
import { COPY } from '../../copy/microcopy';
import scenarioData from '../../data/scenario.json';
import { BrandBackdrop } from '../../ui';
import { savePlayerScore, fetchTopScores, updatePlayerNickname, type LeaderboardRow } from '../../utils/supabase';

interface ResultsScreenProps {
  onRestart: () => void;
}

const taskTitles = Object.fromEntries(
  scenarioData.tasks.map((t) => [t.id, t.title]),
);

function ResiCraftWordmark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 514 152" role="img" aria-label="ResiCraft" xmlns="http://www.w3.org/2000/svg">
      <rect x="25" y="32" width="88" height="88" rx="28.16" fill="#FFD41F" />
      <path fillRule="evenodd" clipRule="evenodd" d="M25 85.3421L57.8258 85.3421C61.3348 92.3199 68.6515 97.1204 77.1101 97.1201C84.8371 97.1201 91.6108 93.1145 95.3997 87.1024L77.8894 87.1024L74.6272 81.5513L71.365 76.0004L74.6272 70.4492L77.8894 64.8984L95.4 64.8984C91.6108 58.8863 84.8371 54.8801 77.1101 54.8801C68.6511 54.8801 61.3348 59.6803 57.8258 66.6587L25 66.6587L25 72.548L43.6728 72.548C45.6055 72.548 47.1867 74.1013 47.1867 76.0001C47.1867 77.8989 45.6055 79.4525 43.6728 79.4525L25 79.4525L25 85.3421Z" fill="#040A1D" />
      <path d="M129 70.1132C129 57.2528 135.635 52 148.72 52H156V60.1509H151.485C142.823 60.1509 139.782 64.4075 139.782 72.9208V100H129V70.1132Z" fill="#FFFFFF" />
      <path d="M181.395 100C165.759 100 163 91.2239 163 75.6828C163 56.4851 169.99 51 186.546 51C200.342 51 210 54.6567 210 70.1978C210 75.6828 207.977 79.3396 202.458 79.3396H174.129C174.129 86.653 175.417 91.7724 184.614 91.7724H208.436V100H181.395ZM196.479 71.1119C198.595 71.1119 198.871 69.6493 198.871 68.278C198.871 61.8787 192.984 59.2276 186.546 59.2276C177.348 59.2276 174.129 62.8843 174.129 71.1119H196.479Z" fill="#FFFFFF" />
      <path d="M217.632 91.8491H244.553C246.721 91.8491 248.979 90.6717 248.979 87.5019C248.979 82.4302 243.288 82.0679 238.952 80.8L229.737 78.2642C219.439 75.4566 217 71.2 217 64.317C217 55.6226 221.065 52 230.731 52H257.742V60.1509H232.357C228.924 60.1509 227.298 60.966 227.298 64.4981C227.298 68.5736 231.273 69.6604 234.796 70.6566L245.095 73.5547C253.586 75.9094 260 77.9925 260 87.7736C260 95.1094 257.38 100 248.256 100H217.632V91.8491Z" fill="#FFFFFF" />
      <path d="M267 45.7627V36H278V45.7627H267ZM267 52.0904H278V100H267V52.0904Z" fill="#FFFFFF" />
      <path d="M308.323 60.1509C297.117 60.1509 296.024 66.8528 296.024 75.9094C296.024 84.966 297.117 91.8491 308.323 91.8491H327V100H305.681C289.282 100 285 90.4 285 75.9094C285 61.4189 289.282 52 305.681 52H325.907V60.1509H308.323Z" fill="#FFFFFF" />
      <path d="M334 70.1132C334 57.2528 340.635 52 353.72 52H361V60.1509H356.485C347.823 60.1509 344.782 64.4075 344.782 72.9208V100H334V70.1132Z" fill="#FFFFFF" />
      <path d="M399.288 52C408.491 52 413 57.9774 413 66.1283V91.8491C413 97.283 410.239 100 404.718 100H380.147C371.865 100 368 95.0189 368 85.0566C368 76 371.865 70.1132 380.147 70.1132H402.233V67.034C402.233 62.5057 400.025 60.1509 395.423 60.1509H371.589V52H399.288ZM384.288 78.2642C380.607 78.2642 379.135 80.5283 379.135 85.0566C379.135 89.5849 380.607 91.8491 384.288 91.8491H399.472C401.313 91.8491 402.233 90.9434 402.233 89.1321V78.2642H384.288Z" fill="#FFFFFF" />
      <path d="M426.827 100V60.226H420V52.0904H426.827C426.827 39.435 436.238 36 448.232 36C449.155 36 450.077 36.0904 451 36.0904V43.8644C450.354 43.774 449.801 43.774 449.155 43.774C443.25 43.774 437.622 44.226 437.622 52.0904H451V60.226H437.622V100H426.827Z" fill="#FFFFFF" />
      <path d="M482.364 100C471.455 100 464.636 96.0686 464.636 84.1829V59.7714H458V51.5429H464.636V36H475.273V51.5429H489V59.7714H475.273V81.9886C475.273 89.3029 479.091 91.7714 486.364 91.7714H489V100H482.364Z" fill="#FFFFFF" />
    </svg>
  );
}

export function ResultsScreen({ onRestart }: ResultsScreenProps) {
  const { state, resetGame } = useGame();
  const score = state.score;

  const [animatedScore, setAnimatedScore] = useState(0);
  const [isCounterFinished, setIsCounterFinished] = useState(false);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [playerNickname, setPlayerNickname] = useState<string | null>(null);
  const [leaderboardRows, setLeaderboardRows] = useState<LeaderboardRow[]>([]);
  const [showLeaderboard, setShowLeaderboard] = useState<boolean>(false);
  const [isEditingNick, setIsEditingNick] = useState<boolean>(false);

  useEffect(() => {
    if (!score) return;
    let startTimestamp: number | null = null;
    const duration = 1000; // 1 second
    const target = score.matchPercentage;
    let animationFrameId: number;

    const step = (timestamp: number) => {
      const now = (typeof globalThis !== 'undefined' && (globalThis as typeof globalThis & { process?: { env?: { NODE_ENV?: string } } }).process?.env?.NODE_ENV === 'test') ? Date.now() : timestamp;
      if (!startTimestamp) startTimestamp = now;
      const progress = Math.min((now - startTimestamp) / duration, 1);
      
      // Easing: easeOutQuad
      const easedProgress = progress * (2 - progress);
      setAnimatedScore(Math.floor(easedProgress * target));

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(step);
      } else {
        setAnimatedScore(target);
        setIsCounterFinished(true);
      }
    };

    animationFrameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationFrameId);
  }, [score]);

  useEffect(() => {
    if (!isCounterFinished || !score) return;

    let active = true;
    const saveAndFetch = async () => {
      try {
        const result = await savePlayerScore(
          score.matchPercentage,
          score.timeSpentSeconds,
          score.verdicts
        );
        if (result && active) {
          const rows = await fetchTopScores();
          if (active) {
            setPlayerId(result.id);
            setPlayerNickname(result.nickname);
            setLeaderboardRows(rows);
            setShowLeaderboard(true);
          }
        } else if (!result && active) {
          setShowLeaderboard(false);
        }
      } catch (err) {
        console.error('Error saving or fetching score:', err);
        if (active) {
          setShowLeaderboard(false);
        }
      }
    };

    saveAndFetch();
    return () => {
      active = false;
    };
  }, [isCounterFinished, score]);

  const handleUpdateNickname = async (newNickname: string) => {
    if (!playerId) return;
    try {
      const success = await updatePlayerNickname(playerId, newNickname);
      if (success) {
        setPlayerNickname(newNickname);
        setIsEditingNick(false);
        const rows = await fetchTopScores();
        setLeaderboardRows(rows);
      } else {
        setShowLeaderboard(false);
      }
    } catch (err) {
      console.error('Error updating nickname:', err);
      setShowLeaderboard(false);
    }
  };

  if (!score) return null;

  const payload = buildExplanationPayload(score);

  const handleRestart = () => {
    resetGame();
    onRestart();
  };

  return (
    <main className="results-screen">
      <BrandBackdrop variant="results" />

      <section className="results-hero" aria-label="Twój wynik">
        <div className="results-hero__top">
          <div className="results-hero__brand">
            <div className="results-hero__brand-mark" aria-hidden="true">
              <ResiCraftWordmark className="results-hero__logo" />
            </div>
          </div>
          <div className="results-hero__chip">AI vs człowiek</div>
        </div>

        <div className="results-hero__content">
          <div className="results-hero__score" aria-live="polite" data-testid="animated-score">
            {animatedScore}%
          </div>
          <h1 className="results-hero__headline">
            {COPY.results.headline(score.matchPercentage)}
          </h1>
          
          {isCounterFinished && (
            <>
              <div className="results-hero__times">
                <span className="results-hero__pill">
                  <strong>{COPY.results.playerTimeLabel}</strong> {score.timeSpentSeconds} s
                </span>
                <span className="results-hero__pill results-hero__pill--accent">
                  <strong>{COPY.results.aiTimeLabel}</strong> {payload.aiTimeMs} ms
                </span>
              </div>
              <p className="results-hero__tagline">{COPY.results.comparisonTagline(score.timeSpentSeconds)}</p>
            </>
          )}
        </div>
      </section>

      <section className="results-grid">
        {isCounterFinished && <ExplanationPanel payload={payload} taskTitles={taskTitles} />}
        <div className="results-side-col">
          {showLeaderboard && (
            <div className="leaderboard">
              <h2 className="leaderboard__title">{COPY.leaderboard.leaderboardTitle}</h2>
              <table className="leaderboard__table">
                <thead>
                  <tr>
                    <th className="leaderboard__th">{COPY.leaderboard.leaderboardHeaderPlayer}</th>
                    <th className="leaderboard__th">{COPY.leaderboard.leaderboardHeaderScore}</th>
                    <th className="leaderboard__th">{COPY.leaderboard.leaderboardHeaderTime}</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboardRows.map((row, index) => {
                    const isCurrentPlayer = row.id === playerId;
                    return (
                      <tr 
                        key={row.id} 
                        className={`leaderboard__row ${isCurrentPlayer ? 'leaderboard__row--highlight' : ''}`}
                      >
                        <td className="leaderboard__td">
                          {index + 1}. {isCurrentPlayer ? `${COPY.leaderboard.leaderboardPlayerHighlight} (${row.nickname})` : row.nickname}
                        </td>
                        <td className="leaderboard__td">{row.score}%</td>
                        <td className="leaderboard__td">{row.time_spent} s</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {playerId && !leaderboardRows.some((r) => r.id === playerId) && (
                <div className="leaderboard__outside-player">
                  <div className="leaderboard__outside-player-title">...</div>
                  <table className="leaderboard__table">
                    <tbody>
                      <tr className="leaderboard__row leaderboard__row--highlight">
                        <td className="leaderboard__td">
                          {COPY.leaderboard.leaderboardPlayerHighlight} ({playerNickname})
                        </td>
                        <td className="leaderboard__td">{score.matchPercentage}%</td>
                        <td className="leaderboard__td">{score.timeSpentSeconds} s</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {playerId && (
                <div className="leaderboard__actions">
                  {!isEditingNick ? (
                    <button 
                      className="leaderboard__button"
                      onClick={() => setIsEditingNick(true)}
                    >
                      {COPY.leaderboard.leaderboardSaveButton}
                    </button>
                  ) : (
                    <form 
                      className="leaderboard__edit-form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        const formData = new FormData(e.currentTarget);
                        const nickname = formData.get('nickname') as string;
                        const cleaned = nickname.trim().slice(0, 20).replace(/[<>/\\{}|[\]]/g, "");
                        if (cleaned) {
                          handleUpdateNickname(cleaned);
                        }
                      }}
                    >
                      <div className="leaderboard__field">
                        <label className="leaderboard__label" htmlFor="nickname-input">
                          {COPY.leaderboard.leaderboardNicknameLabel}
                        </label>
                        <input
                          id="nickname-input"
                          name="nickname"
                          type="text"
                          className="leaderboard__input"
                          defaultValue={playerNickname || ''}
                          maxLength={20}
                          required
                          autoFocus
                        />
                      </div>
                      <button type="submit" className="leaderboard__button leaderboard__button--confirm">
                        {COPY.leaderboard.leaderboardConfirmButton}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>
          )}
          <LeadForm />
        </div>
      </section>

      <button className="results-screen__restart" onClick={handleRestart}>
        {COPY.results.restartButton}
      </button>
    </main>
  );
}
